/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSelector } from '@reduxjs/toolkit';

import { CustomRole, Role, ApiStatus } from '../../../../ts';
import type { Assistant } from '../../../../ts/assistant';
import { SkillType } from '../../../../ts/skill';
import type { RootState } from '../../../../ts/state';
import { isWxoEnabled } from '../../util/config';
import {
  getAssistantRole,
  isAccessControlOn,
  iaOngoingMaintenanceReadOnly
} from '../access-control/selectors';
import { getAssistantId } from '../session/selectors';
import {
  selectAllAssistants,
  selectAssistant,
  selectAssistantById,
  selectSkillReferencesFromAssistant
} from './core-selectors';

export const selectReadableAssistants = (state: RootState): Assistant[] => {
  const allAsst = selectAllAssistants(state);
  const isAccessControlEnabled = isAccessControlOn(state);
  if (isAccessControlEnabled) {
    // presence of role means at least Reader (others Writer, Manager)
    return allAsst.filter(({ assistant_id }) =>
      Boolean(getAssistantRole(state, assistant_id))
    );
  } else {
    return allAsst;
  }
};

/////////////////////
// NOTES on CustomRoles
//   Assistant uses the following "custom roles" which are defined by our product and
// get assigned in IAM by instance or assistant-id (etc) similar to the stock roles like
// Reader or Writer.
// At this writing (2Q23) there are two custom roles:
//  LogsReader - if held, the user can load the Analytics pages (normally need Manager)
//  VersionMaker - holder can make a version even if lacking Writer role
//
// Note that each has a _DEV flavor because of IAM limitations -- ie LOGS_READER_DEV can
// be assigned to dev slot "assistant.dev" items, while LOGS_READER is for Prod and PSTG non-.dev
//
///////

export const selectIsLogReaderForAssistant = (
  state: RootState,
  assistantId: string
): boolean => {
  if (!isAccessControlOn(state)) {
    return true;
  }
  const assistant = selectAssistantById(state, assistantId);
  return (
    assistant.role === Role.MANAGER ||
    (isWxoEnabled && assistant.role === Role.WRITER) ||
    assistant.custom_roles?.some(
      entry =>
        entry === CustomRole.LOGS_READER || entry === CustomRole.LOGS_READER_DEV
    )
  );
};

export const selectIsVersionMakerForAssistant = (
  state: RootState,
  assistantId: string
): boolean => {
  if (!isAccessControlOn(state)) {
    return true;
  }
  const assistant = selectAssistantById(state, assistantId);
  return (
    assistant.role === Role.MANAGER ||
    assistant.role === Role.WRITER ||
    assistant.custom_roles?.some(
      entry =>
        entry === CustomRole.VERSION_MAKER ||
        entry === CustomRole.VERSION_MAKER_DEV
    )
  );
};

export const selectIsVersionMakerForCurrentAssistant = (
  state: RootState
): boolean => {
  if (iaOngoingMaintenanceReadOnly(state)) {
    return false;
  }
  const currentAssistantId = getAssistantId(state);
  return selectIsVersionMakerForAssistant(state, currentAssistantId);
};

export const selectAssistantName = (state: RootState): string | null => {
  const assistant = selectAssistant(state);
  if (assistant && assistant.name) {
    return assistant.name;
  }
  return null;
};

export const isUpdateStatusLoading = (state: RootState): boolean =>
  state.assistants.updateStatus === ApiStatus.LOADING;

/**
 * Returns true if dialog is enabled in the selected assistant.
 * This check is deprecated and may not work as expected.
 *
 * Use `selectIsDialogEnabledInDraftEnvironment`.
 *
 * @deprecated Use {selectIsDialogEnabledInDraftEnvironment}
 */
export const isDialogSkillEnabled = createSelector(
  [selectAssistant],
  assistant => {
    const skill = selectSkillReferencesFromAssistant(assistant)?.find(
      skill => skill.type === SkillType.DIALOG
    );

    return skill && !skill.disabled;
  }
);
